// ════════════════════════════════════════════════════════════════════
// TERROR — H14 · CARD LORE
// ────────────────────────────────────────────────────────────────────
// "Every card in the Codex gets a lore paragraph; Willee reads them
//  aloud in THE RECORD when browsed from the Dungeon."
//                       — the roadmap, H14 (drafts V'ael's, veto caP'n's)
//
// ── THE DECISION: A GENERATOR PLUS AN OVERRIDE, NOT 181 PARAGRAPHS ───
// The obvious build is a table with one hand-written paragraph per card.
// It is also the wrong build, for a reason that has nothing to do with
// effort:
//
//   A table of 181 paragraphs is 181 things that can silently disagree
//   with the card. Rewrite a Lord's stats and its lore still calls it
//   fragile. Move a being between ilks and its paragraph still names the
//   old one. Nobody re-reads 181 paragraphs after a balance pass.
//
// So the DEFAULT paragraph is COMPOSED FROM THE CARD ITSELF — its line,
// its rank, its ilk, its school, its stat shape, its own flavour text.
// It cannot drift, because there is nothing to drift from: change the
// card and the paragraph changes with it. Every one of the 181 has a
// readable page today, and none of them will ever be wrong.
//
// The AUTHORED table then overrides, one card at a time, forever. That
// is where caP'n's real canon goes, and a card he has written is a card
// the generator never touches again.
//
// ── WHAT MAKES A GENERATED PARAGRAPH READ LIKE PROSE ─────────────────
// Not randomness — a lore page that says something different on each
// visit is a lore page nobody believes. Every choice below is keyed off
// a STABLE property of the card (its ilk, its rank, a hash of its id),
// so a card's paragraph is the same paragraph every time anybody opens
// it, on any device, forever. Deterministic variety, not variety.
//
// ── STATUS ───────────────────────────────────────────────────────────
// ⚠ EVERY generated line and every seeded authored entry below is
// V'ael's placeholder prose. `isAuthored(id)` and `loreStatus()` exist
// so a future session can see at a glance what is canon and what is
// still scaffolding — and so the Codex can mark it, which it does.
// ════════════════════════════════════════════════════════════════════

import { CARD_TYPES, SCHOOLS } from "./cards";

// ── ⭐ THE REGISTER, AFTER THE 2026-08-18 LORE DROP ───────────────────
// Two rulings that change how every page should read:
//
// 1. AN AENARCHIST IS UNLICENSED, not chosen. caP'n: the Unstable World
//    "sounds insane but it's actually very much like society, just on
//    steroids — very bureaucratic. There are millions of schools,
//    kingdoms, so many places to learn the Arts and be formally
//    recognised. Those unrecognised are Aenarchists."
//    So the world is BUREAUCRATIC, not lawless, and the player is not a
//    chosen one — they are somebody with the same power as the licensed
//    and none of the paperwork. Lore may reference seals, ledgers,
//    schools, ranks and recognition as ordinary furniture.
//
// 2. J'YNXI IS WEATHER. "Indifferent. True Chaos — controlled
//    randomness that is scary. Not good, not bad. Only there to cause
//    fortune or misfortune; nature's equaliser." She must never read as
//    malicious and never as kind.
//
// Neither is stated flatly anywhere player-facing. Both are the air the
// prose breathes.

// ── THE VOICE OF EACH SCHOOL ───────────────────────────────────────────
// One clause per School, per rank. The Unstable World's four Schools —
// Space, Phantom, Dragon, Time — are the spine of every being's
// identity, so the paragraph opens on the School and everything else
// hangs off it.
// ⚠ RENAMED 2026-08-25 (ILK_VOICE -> SCHOOL_VOICE, keyed off SCHOOLS
// instead of ILKS). These four words were always the Family-B axis;
// only their field name was wrong, and the prose below is untouched.
const SCHOOL_VOICE = {
  [SCHOOLS.SPACE]: {
    noun: "the distance between things",
    beb: "It arrived the way light arrives — later than it left, and certain it is on time.",
    champion: "Distance stopped being something it crossed and started being something it carried.",
    lord: "It does not travel. The far side simply agrees to be nearer while it is looking.",
  },
  [SCHOOLS.PHANTOM]: {
    noun: "the part of a thing that is left over",
    beb: "Nothing here was born. Something here was left behind and got used to it.",
    champion: "It has learned that being uncounted is not the same as being absent.",
    lord: "The tally has always skipped this line. It was never an oversight.",
  },
  [SCHOOLS.DRAGON]: {
    noun: "the fire that outlives what it was lit for",
    beb: "Small, and already too warm to hold.",
    champion: "It burns to a purpose now, which is worse for everyone standing near it.",
    lord: "There is no fuel left in it and it has not gone out. Nobody has explained this.",
  },
  [SCHOOLS.TIME]: {
    noun: "the hour that refuses to be spent",
    beb: "It is running slightly behind and has decided that everyone else is early.",
    champion: "It has stopped keeping time and started keeping accounts.",
    lord: "It has been at this a very long while, and it is not tired, and that is the frightening part.",
  },
};

const RANK_WORD = { 1: "Beb", 2: "Champion", 3: "Lord" };
const RANK_KEY = { 1: "beb", 2: "champion", 3: "lord" };

// ── THE STAT READ ─────────────────────────────────────────────────────
// Lyt+Lyf is fixed per rank (8 / 9 / 10) and the DISTRIBUTION is the
// card's whole personality. Reading it back in prose is the single most
// card-specific thing this file can say without an author, and it can
// never be wrong because it is computed from the numbers on the card.
function statSentence(card) {
  const lyt = card.lyt ?? null, lyf = card.lyf ?? null;
  if (lyt == null || lyf == null) return null;
  if (lyf === 0) return "It has nothing held back at all — every scrap of it is edge, and the first thing to reach it ends it.";
  if (lyt === 0) return "It does not strike. It endures, and waits for the striking to be somebody else's problem.";
  const d = lyt - lyf;
  if (d >= 4) return "Almost all of it is the swing. What is left over would not survive a serious answer.";
  if (d >= 2) return "It leans forward. There is enough behind the swing to survive one mistake, not two.";
  if (d <= -4) return "Almost none of it is the swing. It is here to still be here.";
  if (d <= -2) return "It gives ground on purpose, and it is very hard to finish.";
  return "It is balanced, which in the Unstable World is its own kind of stubbornness.";
}

// ── THE OTHER TYPES ───────────────────────────────────────────────────
// A Tome is not a being and must not be written like one. Each
// non-Escendant type gets its own opening, keyed off the fields that
// type actually carries.
const TYPE_OPENER = {
  [CARD_TYPES.ESCLING]: () =>
    "Before it is anything, it is this: essence with a shape and no history. Everything an Escling becomes, it becomes after somebody decides for it.",
  [CARD_TYPES.TOME]: (c) =>
    `A ${String(c.ilk || "written").toLowerCase()} working, set down so it could be done again by somebody who was not there the first time.`,
  [CARD_TYPES.IMPERA]: () =>
    "An Impera is not a gift. It is a rung, and the thing that climbs it does not come back down the same.",
  [CARD_TYPES.UHLTI]: () =>
    "An U'hlti is what a being does when it stops rationing itself. It attaches before the Affinity because the order is the point — power first, then what the power is for.",
  [CARD_TYPES.AFFINITY]: (c) =>
    `${c.affinityType || "An affinity"} is not a strategy. It is an allegiance, and the Unstable World keeps a list.`,
  [CARD_TYPES.CURIO]: () =>
    "Somebody wore this. It did not help them, and it is still here, which tells you what it thinks of being worn.",
  [CARD_TYPES.PROPHECY]: () =>
    "It was written before there was anybody to read it, and it has been waiting for the board to look like this.",
};

// A stable small integer from an id — so a card that has two equally
// good closing lines always gets the SAME one. Deterministic, not
// random; see the note at the top.
function idHash(s) {
  let h = 0;
  for (let i = 0; i < String(s).length; i++) h = ((h << 5) - h + String(s).charCodeAt(i)) | 0;
  return Math.abs(h);
}

const CLOSERS = [
  "The Codex records it without comment, which is the Codex's way of being impressed.",
  "Nothing in the Grimoire agrees on where it came from. Everything in the Grimoire agrees it is here.",
  "It has been drawn, played and buried more times than anyone has counted. It has never once looked surprised.",
  "Willee has a longer version of this. Willee always has a longer version.",
  // ⚠ WAS: "Whatever it was before the Rise, it does not answer to that
  // name now." REVERTED 2026-08-18 on caP'n's ruling. THE RISE is real
  // canon but it is element 6 of the Unstable World's LANDSCAPE
  // doctrine — motes going UP, "direction is characterisation,
  // everything in this Empire rises." A visual law, not a date. Reading
  // it as a historical event was V'ael's invention, and Terror already
  // has the rupture everything is dated from: the sealing, "in a time
  // before the Unstable World was Au Gratin." Two competing cataclysms
  // make a timeline mushier, not richer.
  //
  // The replacement says the same thing — this is old and it has
  // outlived its own name — without dating it from anything.
  "It has outlasted the name it was given, and most of the people who gave it.",
];

// ── THE GENERATOR ─────────────────────────────────────────────────────
// Pure, deterministic, and derived only from the card. Safe in a render.
export function generateLore(card) {
  if (!card) return "";
  const parts = [];

  const opener = TYPE_OPENER[card.type];
  if (opener) parts.push(opener(card));

  if (card.type === CARD_TYPES.ESCENDANT || card.type === CARD_TYPES.ESCLING) {
    const voice = SCHOOL_VOICE[card.school];
    if (voice) {
      const rank = RANK_KEY[card.level] || "beb";
      parts.push(voice[rank]);
      if (card.type === CARD_TYPES.ESCENDANT) {
        // A Dragon-ilk being of the Dragon School must not read "a
        // dragon of the Dragon School". Dragon is the one word that sits
        // on BOTH axes — a quarter of the roster, not an edge case —
        // and it is exactly the kind of seam a hand-written table of
        // 181 paragraphs would get right once and wrong forever after
        // the next word is added.
        // (`card.ilk` is Humanoid/Beast/Machine/Dragon and `card.school`
        // is Space/Phantom/Dragon/Time since the 2026-08-25 fix. The
        // sentence names the SAME two words it always did — the noun is
        // the Family-A word, the named axis is the Family-B word — so
        // every paragraph reads as it did, with "ilk" corrected to
        // "School" because that is what the named axis actually is.)
        const ilkWord = String(card.ilk || "being").toLowerCase();
        const rank = RANK_WORD[card.level] || "its own";
        parts.push(
          String(card.school) === String(card.ilk)
            ? `${card.school} through and through — ${voice.noun} — standing at ${rank} rank.`
            : `A ${ilkWord} of the ${card.school} School — ${voice.noun} — standing at ${rank} rank.`
        );
      }
    }
    const st = statSentence(card);
    if (st) parts.push(st);
  }

  // The card's OWN flavour text is the last word before the closer,
  // because it is the one line on this page that is already canon.
  if (card.flavorText) parts.push(`“${card.flavorText}”`);
  else if (card.effect) parts.push(`What it does, it does plainly: ${card.effect}`);

  parts.push(CLOSERS[idHash(card.id) % CLOSERS.length]);
  return parts.filter(Boolean).join(" ");
}

// ── THE AUTHORED TABLE ────────────────────────────────────────────────
// caP'n's canon goes here, one card at a time, forever. A card in this
// table is a card the generator never touches again.
//
// ⚠ The entries seeded below are V'ael's, written as EXAMPLES of the
// register rather than as canon — they show what an authored entry looks
// like next to a generated one. Overwrite freely; deleting one simply
// hands the card back to the generator, which is a safe place to land.
export const AUTHORED_LORE = {
  h01_beb: "The H-01 line was the first thing the Unstable World made that could be reasoned with, and it has never entirely forgiven it for that. A Blue Giant talks first — not out of mercy, but because it has run the numbers on what happens after talking and would rather not. It is the youngest thing in the Grimoire that already sounds tired.",

  escling_space: "Every Escling is the same argument: a shape with nothing written in it yet. The Space Escling is the version of that argument that has already noticed how far away everything is, and has not yet decided whether that is a problem or a door.",


  aff_holy: "Y'Tyranuz is not kindness. It is the belief that one attack, somewhere, ought to simply not have happened — and the authority to make that true exactly once. Aenarchists who take the Holy allegiance describe it the same way every time: not a shield, a veto.",

  // ⭐ Rewritten 2026-08-18 on caP'n's ruling: J'ynxi is INDIFFERENT.
  // "True Chaos — controlled randomness that is scary. Not good, not
  // bad. Only there to cause fortune or misfortune; nature's
  // equaliser." So she must never read as malicious, and never as
  // kind. She reads as WEATHER.
  aff_jynxi: "She does not require anything, and that is not generosity — it is indifference wearing a grin. Every other allegiance in the Unstable World asks for something up front, so you know what it costs. J'ynxi's Wink is freely given, and what it does to you afterward is not a punishment or a gift. It is the weather. Nature keeps one hand free for evening things out, and this is that hand.",

  tome_khao_garden: "The first Khao Garden was an accident — somebody left Aenergy where it could pool and came back to find it growing. Every one since has been on purpose, and none of them have been as good. It is a small working that makes tomorrow slightly larger, which is the most any garden has ever done.",
};

export const isAuthored = (cardId) => Object.prototype.hasOwnProperty.call(AUTHORED_LORE, cardId);

// The one function every surface calls. Authored wins; otherwise
// generated. Never empty, never throws, never asks who wrote it.
export function loreFor(card) {
  if (!card) return "";
  return AUTHORED_LORE[card.id] || generateLore(card);
}

// ── THE STATUS READ ───────────────────────────────────────────────────
// So a session can ask "how much of the Codex is actually written" and
// get a number instead of a feeling. Pass getAllGrimoireCards().
export function loreStatus(allCards = []) {
  const authored = allCards.filter((c) => isAuthored(c.id));
  return {
    total: allCards.length,
    authored: authored.length,
    generated: allCards.length - authored.length,
    pct: allCards.length ? Math.round((authored.length / allCards.length) * 100) : 0,
    authoredIds: authored.map((c) => c.id),
  };
}

const cardLore = { generateLore, AUTHORED_LORE, isAuthored, loreFor, loreStatus };
export default cardLore;
